import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { cn } from '@/utils';
import { NavLanguages } from '@/assets/nav-icons';
import { useSafeTranslation } from '@/hooks/useSafeTranslation';

interface LanguageSwitcherProps {
  variant?: 'header' | 'menu' | 'floating';
  className?: string;
}

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' }
];

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  variant = 'header',
  className 
}) => {
  const { t } = useSafeTranslation();
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  if (variant === 'floating') {
    return (
      <div className={cn("relative", className)}>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsOpen(!isOpen)}
          className="bg-white shadow-medium rounded-full p-3 flex items-center space-x-2 border border-gray-200 hover:shadow-large transition-all"
        >
          <span className="text-xl">{currentLanguage.flag}</span>
          <NavLanguages className="w-4 h-4 text-gray-600" />
        </motion.button>

        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)} 
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-large border border-gray-200 py-2 z-50 min-w-[140px]"
            >
              {languages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => changeLanguage(language.code)}
                  className={cn(
                    "w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors",
                    i18n.language === language.code && "bg-primary-50 text-primary-600"
                  )}
                >
                  <span className="text-lg">{language.flag}</span>
                  <span className="font-medium">{language.name}</span>
                </button>
              ))}
            </motion.div>
          </>
        )}
      </div>
    );
  }

  if (variant === 'menu') {
    return (
      <div className={cn("relative", className)}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-gray-50 rounded-lg transition-colors"
        >
          <div className="flex items-center space-x-3">
            <NavLanguages className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-700">{t('ui.language')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm">{currentLanguage.flag}</span>
            <span className="text-xs text-gray-500">{currentLanguage.code.toUpperCase()}</span>
          </div>
        </button>

        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)} 
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-large border border-gray-200 py-1 z-50"
            >
              {languages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => changeLanguage(language.code)}
                  className={cn(
                    "w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors",
                    i18n.language === language.code && "bg-primary-50 text-primary-600"
                  )}
                >
                  <span>{language.flag}</span>
                  <span className="font-medium text-sm">{language.name}</span>
                </button>
              ))}
            </motion.div>
          </>
        )}
      </div>
    );
  }

  // Header variant (default)
  return (
    <div className={cn("relative", className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex min-h-11 items-center space-x-2 rounded-lg px-2 transition-colors hover:bg-gray-100 sm:px-3"
      >
        <span className="text-lg">{currentLanguage.flag}</span>
        {/* The code is redundant beside the flag on a phone, and dropping it
            buys the restaurant name back ~24px in a tight header. */}
        <span className="hidden text-sm font-medium text-gray-700 sm:inline">{currentLanguage.code.toUpperCase()}</span>
        <NavLanguages className="w-4 h-4 text-gray-500" />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)} 
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-large border border-gray-200 py-1 z-50 min-w-[120px]"
          >
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => changeLanguage(language.code)}
                className={cn(
                  "w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center space-x-2 transition-colors text-sm",
                  i18n.language === language.code && "bg-primary-50 text-primary-600"
                )}
              >
                <span>{language.flag}</span>
                <span className="font-medium">{language.name}</span>
              </button>
            ))}
          </motion.div>
        </>
      )}
    </div>
  );
};